import React from 'react';
import { Box, Button, Card, CardContent, Flex, Switch, Text, TextField, useToast } from '@chatsdet/ui';
import { Info } from '@phosphor-icons/react';
import { normalizeTestSignInDomain } from '@chatsdet/types';
import { CopyValueButton } from '../CopyValueButton';
import { useUpdateProviderMutation, useTestSignInQuery } from './useTestSignIn';

type Provider = NonNullable<ReturnType<typeof useTestSignInQuery>['data']>['data']['provider'];

export function TestSignInProviderCard({
  provider,
  isAdmin,
}: {
  provider: Provider;
  isAdmin: boolean;
}) {
  const { toast } = useToast();
  const [isEditingDomain, setIsEditingDomain] = React.useState(false);
  const [domainDraft, setDomainDraft] = React.useState(provider?.emailDomain ?? '');
  const updateProvider = useUpdateProviderMutation();

  React.useEffect(() => {
    setDomainDraft(provider?.emailDomain ?? '');
  }, [provider?.emailDomain]);

  if (!provider) return null;

  const domainValid = normalizeTestSignInDomain(domainDraft);

  const handleSaveDomain = async () => {
    if (!domainValid) return;
    try {
      await updateProvider.mutateAsync({ emailDomain: domainValid });
      setIsEditingDomain(false);
      toast.success('Email domain updated');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update email domain');
    }
  };

  return (
    <Card padding="lg">
      <CardContent>
        <Flex align="start" justify="between" gap="md" wrap>
          <Box className="space-y-3 min-w-0 flex-1">
            <Box>
              <Text size="xs" weight="semibold" className="uppercase tracking-wide text-muted-foreground">
                Issuer
              </Text>
              <Flex align="center" gap="xs">
                <Text size="sm" className="font-mono break-all">
                  {provider.issuer}
                </Text>
                <CopyValueButton value={provider.issuer} label="issuer URL" />
              </Flex>
            </Box>
            <Box>
              <Text size="xs" weight="semibold" className="uppercase tracking-wide text-muted-foreground">
                Discovery document
              </Text>
              <Flex align="center" gap="xs">
                <Text size="sm" className="font-mono break-all">
                  {provider.discoveryUrl}
                </Text>
                <CopyValueButton value={provider.discoveryUrl} label="discovery URL" />
              </Flex>
            </Box>
            <Box>
              <Text size="xs" weight="semibold" className="uppercase tracking-wide text-muted-foreground">
                Test-user email domain
              </Text>
              {isEditingDomain ? (
                <Flex align="center" gap="sm" className="mt-1">
                  <Box className="max-w-xs">
                    <TextField
                      aria-label="Email domain"
                      value={domainDraft}
                      onChange={setDomainDraft}
                      isDisabled={updateProvider.isPending}
                    />
                  </Box>
                  <Button
                    size="sm"
                    variant="primary"
                    onPress={handleSaveDomain}
                    isDisabled={!domainValid || updateProvider.isPending}
                  >
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onPress={() => {
                      setDomainDraft(provider.emailDomain);
                      setIsEditingDomain(false);
                    }}
                    isDisabled={updateProvider.isPending}
                  >
                    Cancel
                  </Button>
                </Flex>
              ) : (
                <Flex align="center" gap="sm">
                  <Text size="sm">@{provider.emailDomain}</Text>
                  {isAdmin && (
                    <Button variant="link" size="sm" onPress={() => setIsEditingDomain(true)}>
                      Edit
                    </Button>
                  )}
                </Flex>
              )}
            </Box>
          </Box>
          {isAdmin && (
            <Flex align="center" gap="sm" className="shrink-0">
              <Text size="sm" className="text-muted-foreground">
                {provider.disabled ? 'Off' : 'On'}
              </Text>
              <Switch
                isSelected={!provider.disabled}
                onChange={(selected) => updateProvider.mutate({ disabled: !selected })}
                isDisabled={updateProvider.isPending}
                aria-label="Test sign-in enabled"
              />
            </Flex>
          )}
        </Flex>
        <Flex align="start" gap="xs" className="mt-4 pt-4 border-t border-border">
          <Info size={14} className="text-muted-foreground mt-0.5 shrink-0" />
          <Text size="xs" className="text-muted-foreground">
            Cofactor never vouches for anyone outside <code className="mx-1">@{provider.emailDomain}</code>.
          </Text>
        </Flex>
      </CardContent>
    </Card>
  );
}
